import { Chip, Stack, SxProps, Typography } from '@mui/material';
import { FC, ReactNode } from 'react';
import { Development } from '~/api/types';
import { colors } from '~/theme';

const RowSx: SxProps = {
  'my': 1,
  'py': 2,
  'px': 3,
  'mx': 'auto',
  'backgroundColor': colors.lightGray,
  'display': 'flex',
  'justifyContent': 'space-between',
  'alignItems': 'center',
  'cursor': 'pointer',
  '&:hover': {
    backgroundColor: colors.gray,
  },
  'maxWidth': '100%',
};

/**
 * A row nothing can be done with anymore. It is never hidden, so that the
 * customer still sees the development and the badge telling why it is inert.
 */
const DisabledRowSx: SxProps = {
  ...RowSx,
  'cursor': 'default',
  '&:hover': {
    backgroundColor: colors.lightGray,
  },
  '& img': {
    opacity: 0.5,
  },
};

const LabelSx: SxProps = {
  textAlign: 'left',
  display: 'block',
  maxWidth: 'calc(100% - 50px)',
};

const ChipSx: SxProps = {
  'background': colors.lightPurple,
  'color': colors.white,
  'fontFamily': 'Inter',
  'fontSize': 12,
  'textTransform': 'uppercase',
  'fontWeight': '700',
  'lineHeight': '150%',
  'height': '18px',
  '& .MuiChip-label': { px: '6.5px' },
};

/**
 * The badge sits under the development name instead of beside it, so that it
 * does not fight the "Imobiliária" chip and the trailing element for the width
 * of the row on a phone. It is also typeset like the rest of the row instead of
 * like the "Imobiliária" chip: it tells the state of the development, so it
 * should read as a label and not shout over the name above it.
 */
const BadgeChipSx: SxProps = {
  ...ChipSx,
  background: colors.darkGray,
  alignSelf: 'flex-start',
  mt: 0.5,
  fontFamily: 'Inter, sans-serif',
  fontWeight: '400',
  textTransform: 'none',
};

const TitleSx: SxProps = {
  fontWeight: 'bold',
  fontSize: { xs: 14, md: 16 },
  wordWrap: 'break-word',
};

const CompanySx: SxProps = {
  color: colors.darkGray,
  fontSize: { xs: 12, md: 14 },
  wordWrap: 'break-word',
};

const DisabledTextSx: SxProps = {
  color: colors.disabledGray,
};

/**
 * The row every development is listed as, in both the screens picking a single
 * one and the ones picking many at once. Only the trailing element differs
 * between them.
 */
export const DevelopmentRow: FC<DevelopmentRowProps> = ({ development, disabled, badgeLabel, onClick, trailing }) => {
  const { companyName, name, logo, isPayfac } = development;

  return (
    <Stack direction={'row'} sx={disabled ? DisabledRowSx : RowSx} onClick={disabled ? undefined : onClick} gap={2}>
      <img src={logo} alt="Building Icon" style={{ width: 48, height: 48, flexShrink: 0 }} />
      <Stack sx={LabelSx} flex={1}>
        <Typography sx={disabled ? { ...CompanySx, ...DisabledTextSx } : CompanySx}>{companyName}</Typography>
        <Typography sx={disabled ? { ...TitleSx, ...DisabledTextSx } : TitleSx}>{name}</Typography>
        {badgeLabel && <Chip label={badgeLabel} sx={BadgeChipSx} />}
      </Stack>
      {isPayfac && <Chip label="Imobiliária" sx={ChipSx} />}
      {trailing}
    </Stack>
  );
};

export interface DevelopmentRowProps {
  development: Development;
  /**
   * Grays the row out and drops its click handler, for the developments there is
   * nothing left to do with.
   */
  disabled?: boolean;
  /**
   * Rendered as a chip before the "Imobiliária" one, to tell something about the
   * state of this development.
   */
  badgeLabel?: string;
  onClick?: () => void;
  /**
   * What closes the row: a chevron when picking a single development, a checkbox
   * when picking many.
   */
  trailing: ReactNode;
}
